import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject, Subscription } from 'rxjs';

import { ENVIRONMENT, Environment } from '@environment';
import { LanguageSelectorComponent } from '@usheru-hff/core/language-selector';
import { HffCommonUiUsheruLogoModule } from '@usheru-hff/hff-common/ui-usheru-logo';
import {
  ShowtimeStreamingSubscribeComponent,
  FormSubscriptionComponent
} from '@usheru-hff/movie-detail/feature-subscribe';
import { ChannelService } from '@usheru-hff/sdk/channel';
import { Channel, PageLink, SocialNetworkLink } from '@usheru-hff/shared/domain';
import { SharedFeatureSocialLinksModule } from '@usheru-hff/shared/feature-social-links';
import { FooterService } from '@usheru-hff/shared/services';
import { SharedUtilsModule, TemplatesService } from '@usheru-hff/shared/utils';
import { SharedUtilsImageModule } from '@usheru-hff/shared/utils-image';
import { NavigationService } from '@usheru-hff/shared/utils-navigation';
import { TrackingService } from '@usheru-hff/sdk/tracking';
import { SharedUtilsTranslationModule } from '@usheru-hff/shared/utils-translation';

/**
 * //TODO barbeitoalex propostal - organice life cycle methods. Split in specific method to improve docs.
 * //TODO barbeitoalex propostal - use env values in their own method and subscribe to env changes
 * //TODO barbeitoalex propostal - subscribe to current language to update the translate pipes
 */
@Component({
  selector: 'ush-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    HttpClientModule,
    ShowtimeStreamingSubscribeComponent,
    FormSubscriptionComponent,
    SharedFeatureSocialLinksModule,
    SharedUtilsTranslationModule,
    LanguageSelectorComponent,
    SharedUtilsModule,
    HffCommonUiUsheruLogoModule,
    SharedUtilsImageModule,
    RouterModule
  ]
})
export class FooterComponent implements OnInit, OnDestroy {
  /**
   * Input to active a class to set the transparent background.
   */
  @Input() transparentBackground?: boolean;

  /**
   * Alternative custom template to show. This is provded by the {@link templateService}.
   */
  @ContentChild(TemplateRef) customTemplate: TemplateRef<unknown>;

  /**
   * Logo image
   */
  logoUrl: string;

  /**
   * Reddirection of the logo. Can be internal or external. No options to be null.
   */
  logoLink: string;

  /**
   * Params to render the {@link logoUrl}
   */
  logoParams: { width?: number; height?: number };

  /**
   * Indicate if the {@link logoLink} is a internal or external link.
   */
  logoLinkExternal = false;

  /**
   * Indicate if the must show the social icons. This is set from enviroment settings.
   */
  hasSocialLinks: boolean;

  /**
   * List of social links to display.
   */
  socialLinks: SocialNetworkLink[];

  /**
   *
   */
  defaultSocialLinks: SocialNetworkLink[];

  /**
   * Stores variables to get in the {@link customTemplate}.
   */
  listOfVariables;

  /**
   * Links to show in the menu.
   */
  links: PageLink[];

  /**
   * @docsNotRequired
   */
  channel: Channel;

  /**
   * Additional custom entry for {@link links}.
   */
  footerCustomLinkTemplate: TemplateRef<unknown>;

  /**
   * Reference to "this" component to use in the {@link footerCustomLinkTemplate}.
   * //TODO barbeitoalex propostal - change any to FooterComponent?
   */
  footerInstance: unknown;

  /**
   * if the mutiplelogos is set, this will be the text to show
   */
  multiplelogosText?: string;
  /**
   * if the logoConfig partners is set, this will be the list of logos to show
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutiplelogos?: any[];

  appOnlyHasMovies = false;

  channelMultipleLanguages: boolean;

  //service outputs
  $customSocialLinkOutuput: Subscription;
  $defaultSocialLinkOutput: Subscription;
  customStyle$ = this.footerService.customStyle$;

  destroy$ = new Subject<void>();

  constructor(
    private channelService: ChannelService,
    private navigationService: NavigationService,
    @Inject(ENVIRONMENT) private env: Environment,
    private templateService: TemplatesService,
    private footerService: FooterService,
    private cd: ChangeDetectorRef,
    private trackingService: TrackingService
  ) {
    const channel = this.channelService.channel;
    this.channel = channel;

    this.appOnlyHasMovies = this.env?.appHasOnlyMovies;

    const logoConfig = this.env?.logosConfig?.footer;

    if (!logoConfig?.partners) {
      this.logoLink = logoConfig?.logoLink || '/home';
      this.logoLinkExternal = this.logoLink.indexOf('http') == 0;
      this.logoUrl = logoConfig?.logoUrl || this.channel.logoUrl;
      const { width, height } = logoConfig || {};
      this.logoParams = { width, height };
    } else {
      this.multiplelogosText = logoConfig?.partnersText;
      this.mutiplelogos = logoConfig?.partners.map(logo => ({
        logoClass: logo.logoClass,
        logoAlt: logo.logoAlt,
        logoUrl: logo.logoUrl || this.channel.logoUrl,
        logoLink: logo.logoLink || '/home',
        logoLinkExternal: logo.logoLink.indexOf('http') == 0,
        logoParams: { width: logo.width, height: logo.height }
      }));
    }

    this.hasSocialLinks = !env?.customPageParams?.headerSocialLinks || env?.customPageParams?.footerSocialLinks;

    this.channelMultipleLanguages = channel?.languages?.length > 1;
  }

  /**
   * Set some variables and the {@link footerCustomLinkTemplate}.
   */
  ngOnInit() {
    this.links = this.navigationService.getFooterLinks().filter(link => link.visibility == 3);
    this.links = this.links.map(item => {
      if (item['link'].includes('#')) {
        const [path, fragment] = item['link'].split('#');
        const firstPathSegment = path.split('/')[1];
        return { ...item, url: `${path}`, fragment: fragment };
      } else {
        const firstPathSegment = item['link'].split('/')[1];
        return { ...item };
      }
    });
    this.footerInstance = this;

    if (this.channel.socialNetworks) {
      const socialLinks = this.channel.socialNetworks.filter(sn => sn.showLink);
      this.socialLinks = socialLinks;
      this.defaultSocialLinks = socialLinks;
    }

    this.listOfVariables = {
      logoUrl: this.logoUrl,
      links: this.links,
      socialLinks: this.socialLinks,
      transparentBackground: this.transparentBackground,
      hasSocialLinks: this.hasSocialLinks,
      copyrightMsg: this.copyrightMsg,
      hasTranslationFooter: this.hasTranslationFooter
    };
    this.loadFooterCustomTemplate();

    this.startServiceOutputs();
  }

  ngOnDestroy() {
    this.$customSocialLinkOutuput.unsubscribe();
    this.$defaultSocialLinkOutput.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  startServiceOutputs() {
    this.$customSocialLinkOutuput = this.footerService.customSocialLinkOutuput.subscribe(links => {
      this.socialLinks = links;
    });
    this.$defaultSocialLinkOutput = this.footerService.defaultSocialLinkOutput.subscribe(() => {
      this.socialLinks = this.defaultSocialLinks;
    });
  }

  /**
   * Get copyrightMsg from env
   */
  get copyrightMsg() {
    return this.env?.copyrightMsg;
  }

  /**
   * //TODO doc
   */
  get hasTranslationFooter() {
    const hasTranslationFooter = this.env?.customPageParams?.translationFooter;
    if (typeof hasTranslationFooter !== 'undefined') {
      return hasTranslationFooter;
    }
    return false;
  }

  /**
   * Get translate key for a term.
   *
   * //TODO barbeitoalex propostal - this could move to the view?
   * @param name
   * @returns
   */
  getTermsMsg(name) {
    return 'public.footer.link.' + name;
  }

  /**
   * Prepare the {@link footerCustomLinkTemplate} if is enable in the env.
   */
  loadFooterCustomTemplate() {
    if (this.env.templateRefs?.footerCustomLinkTemplate) {
      this.footerCustomLinkTemplate = this.templateService.get(this.env.templateRefs.footerCustomLinkTemplate);
    }
  }

  onFooterLinkClicked(link: PageLink) {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    this.trackingService.trackEvent({
      action: 'FooterLink',
      properties: {
        category: 'Footer',
        label: link.name
      }
    });
  }
}
